import { EventApi } from '@fullcalendar/core';
import { Draggable } from '@fullcalendar/interaction';
import React, { useEffect, useRef } from 'react';
import { FullCalendarEvent } from 'shared/components/ui/full-calendar-event';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { DataType } from 'shared/types/data-type';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';

import * as Styled from './more-popover.style';

export type FullCalendarMorePopoverProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children'
> & {
  events: EventApi[];
  tasks: Task[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  onComplete: (id: ID, date: Timestamp) => void;
  onClick?: (id: ID) => void;
};

export const FullCalendarMorePopover: React.FC<
  FullCalendarMorePopoverProps
> = ({
  events,
  tasks,
  goals,
  lifeAreas,
  onComplete,
  onClick,
  onOutside,
  ...rest
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const draggable = new Draggable(containerRef.current, {
      itemSelector: '[data-draggable="true"]',
      eventData: (el) => {
        const id = el.getAttribute('data-id');
        const title = el.getAttribute('data-title');
        const componentType = el.getAttribute(
          'data-component-type',
        ) as DataType; // Cast to DataType

        if (!id) {
          return;
        }

        // Prepare extendedProps based on the component type
        const extendedProps: Record<string, string> = {};
        if (componentType === DataType.Goal) {
          extendedProps.goalId = id;
        } else if (componentType === DataType.Task) {
          extendedProps.taskId = id;
        } else if (componentType === DataType.LifeArea) {
          extendedProps.lifeAreaId = id;
        }

        return {
          id,
          title,
          extendedProps,
        };
      },
    });

    return () => draggable.destroy();
  }, []);

  return (
    <PopupMenu
      ref={containerRef}
      noBackdrop
      onOutside={onOutside}
      {...rest}
      ignoreSelector="[data-interaction-for-type]"
    >
      <PopupMenuList>
        {events.map((event) => (
          <PopupMenuListItem
            key={event.id}
            data-draggable="true"
            data-component-type={
              event.extendedProps.taskId
                ? DataType.Task
                : event.extendedProps.lifeAreaId
                  ? DataType.LifeArea
                  : DataType.Goal
            }
            data-id={
              event.extendedProps.taskId ||
              event.extendedProps.lifeAreaId ||
              event.extendedProps.goalId
            }
            data-title={event.title}
          >
            <Styled.EventContainer>
              <FullCalendarEvent
                event={event}
                tasks={tasks}
                goals={goals}
                lifeAreas={lifeAreas}
                onComplete={onComplete}
                onClick={onClick}
                onInteractionComplete={onOutside}
              />
            </Styled.EventContainer>
          </PopupMenuListItem>
        ))}
      </PopupMenuList>
    </PopupMenu>
  );
};
